import { useState } from 'react';
import { useList } from '@hana/data';
import { IconArrowBackUp, IconChevronLeft, IconDownload, IconHistory, IconLink, IconLock, IconPlus, IconSend, IconSparkles, IconDeviceFloppy, IconAlertTriangle, IconFileText, IconPhoto } from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { Button, Chip, Segmented, Skeleton, StateView, Textarea, Input, useToast, type ScreenState, type Tone } from '@hana/ui';
import { CANDS, DESC0, JOBS, ORIGINS, PEND, POLICY, RECENT, ftaTable, taxCards, type Dir } from './hs';
import { useT } from '@hana/i18n';
import './i18n';
import s from './hs.module.css';

// Mint-HS (1536): Tư vấn HS · Ask HANA đề xuất 3 mã, biểu thuế 2026, so sánh FTA. Kết quả giả lập; nút ghi báo toast.

type Cf = 'wait' | 'ok' | 'back';
const CF: Record<Cf, [string, Tone]> = { wait: ['hs.cfWait', 'warn'], ok: ['hs.cfOk', 'ok'], back: ['hs.cfBack', 'err'] };
const DOT: Record<string, string> = { warn: 'var(--warn)', ok: 'var(--ok)', err: 'var(--err)', info: 'var(--link)' };

// O-DT: tên mã HS thật từ /v1/hs (chỉ đọc); lỗi / chưa đăng nhập → giữ dữ liệu mẫu.
function HsName({ code, fallback }: { code: string; fallback: string }) {
  const q = useList('hsCode', { where: { code: code.replace(/\./g, '').slice(0, 6) }, limit: 1 });
  return <>{q.data?.items[0]?.name || fallback}</>;
}

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

export function HsAdvice() {
  const t = useT();
  const toastFn = useToast();
  const toast = (msg: string) => toastFn({ msg });
  const state = readState();
  const ro = state === 'noperm';
  const result = state === 'ready' || ro;
  const [dir, setDir] = useState<Dir>('NK');
  const [origin, setOrigin] = useState('Trung Quốc');
  const [desc, setDesc] = useState(DESC0);
  const [pick, setPick] = useState(0);
  const [cf, setCf] = useState<Cf>('wait');
  const [dock, setDock] = useState<'recent' | 'pend' | null>(null);
  const [jobOpen, setJobOpen] = useState(false);
  const code = CANDS[pick]![0];
  const ori = ORIGINS[dir].includes(origin) ? origin : ORIGINS[dir][0]!;
  const fta = ftaTable(ori, pick);
  const cards = taxCards(pick, ori);
  const [cfK, cfTone] = CF[cf];
  const act = cf !== 'wait' || ro || !result;
  const log: [string, string, string][] = [
    [t('hs.log1'), '22/Oct/2026 10:12', 'info'],
    cf === 'ok' ? [t('hs.log2ok'), '22/Oct/2026 10:40', 'ok'] : cf === 'back' ? [t('hs.log2back'), '22/Oct/2026 10:40', 'err'] : [t('hs.log2wait'), t('hs.log2waitSub'), 'warn'],
  ];
  const list = dock === 'pend' ? PEND : RECENT;
  const off = !result;

  return (
    <Page kind="table">
      <PageHeader
        lead={<a href="/ezy/service-tasks" className={s.back} aria-label={t('hs.backSvc')} title={t('hs.backSvc')}><IconChevronLeft size={17} aria-hidden="true" /></a>}
        title={t('hs.title')}
        meta={<Chip tone={cfTone} dot={DOT[cfTone]}>{t(cfK)}</Chip>}
        sub={t('hs.sub')}
        noFind
        actions={
          <>
            <div className={s.pop}>
              <Button aria-label={t('hs.attach')} icon={<IconLink size={16} aria-hidden="true" />} disabled={off} aria-haspopup="true" aria-expanded={jobOpen} onClick={() => setJobOpen((o) => !o)} />
              {jobOpen && (
                <div role="dialog" aria-label={t('hs.pickJob')} className={s.jobs}>
                  <div className={s.jobsH}>{t('hs.openJobs')}</div>
                  {JOBS.map(([k, sub]) => (
                    <button key={k} type="button" className={s.jobRow} onClick={() => { setJobOpen(false); toast(t('hs.noWrite')); }}>
                      <b>{k}</b><div>{sub}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button icon={<IconSend size={16} aria-hidden="true" />} disabled={off} onClick={() => toast(t('hs.noWrite'))}>{t('hs.sendCust')}</Button>
            <Button aria-label={t('hs.pdf')} icon={<IconDownload size={16} aria-hidden="true" />} disabled={off} onClick={() => toast(t('hs.pdfDone', { code }))} />
            <Button variant="primary" aria-label={t('hs.saveCat')} title={ro ? t('hs.noSavePerm') : cf !== 'ok' ? t('hs.needConfirm') : undefined} icon={<IconDeviceFloppy size={16} aria-hidden="true" />} disabled={ro || off || cf !== 'ok'} onClick={() => toast(t('hs.noWrite'))} />
            <Button aria-label={t('hs.recent')} aria-pressed={!!dock} icon={<IconHistory size={18} aria-hidden="true" />} onClick={() => setDock((d) => (d ? null : 'recent'))} />
          </>
        }
      />
      {ro && <div role="note" className={s.note}><IconLock size={16} aria-hidden="true" />{t('hs.roNote')}</div>}
      <div className={s.split}>
      <div className={s.cols}>
        <div className={s.left}>
          <section aria-label={t('hs.goodsInfo')} className={`${s.card} ${s.pad18}`}>
            <h2>{t('hs.goodsH')}</h2>
            <div role="radiogroup" aria-label={t('hs.dir')} className={s.seg}>
              {([['NK', t('hs.imp')], ['XK', t('hs.exp')]] as [Dir, string][]).map(([v, k]) => <button key={v} type="button" role="radio" aria-checked={dir === v} className={`${s.segB} ${dir === v ? s.segOn : ''}`} onClick={() => { setDir(v); setOrigin(ORIGINS[v][0]!); }}>{k}</button>)}
            </div>
            <Input label={t('hs.customer')} value="VINAVARIETY" readOnly />
            <Textarea label={t('hs.descL')} aria-label={t('hs.descL')} required rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} />
            <div>
              <div className={s.lab}>{t('hs.photos')}</div>
              <div className={s.photos}>
                <div className={s.ph}><IconPhoto size={22} aria-hidden="true" />{t('hs.photo1')}</div>
                <div className={s.ph}><IconFileText size={22} aria-hidden="true" />{t('hs.catalogue')}</div>
                <button type="button" className={s.add} disabled={ro} onClick={() => toast(t('hs.photoAdded'))}><IconPlus size={16} aria-hidden="true" />{t('hs.add')}</button>
              </div>
            </div>
            <div>
              <Input label={t('hs.custCode')} value="8516.79.90" readOnly hint={<span className={s.warnTx}><IconAlertTriangle size={13} aria-hidden="true" />{t('hs.diffAi')}</span>} />
            </div>
            <div>
              <div className={s.lab}>{dir === 'NK' ? t('hs.originImp') : t('hs.originExp')}</div>
              <div className={s.origins}>
                {ORIGINS[dir].map((k) => <button key={k} type="button" aria-pressed={ori === k} className={`${s.org} ${ori === k ? s.orgOn : ''}`} onClick={() => setOrigin(k)}>{k}</button>)}
              </div>
            </div>
            <button type="button" className={s.ai} disabled={ro} onClick={() => { setPick(0); setCf('wait'); toast(t('hs.reAsked')); }}><IconSparkles size={16} aria-hidden="true" />{t('hs.askAi')}</button>
          </section>
          <section aria-label={t('hs.confirmSec')} className={`${s.card} ${s.pad16}`}>
            <div className={s.hrow}><h2>{t('hs.confirmH')}</h2><span className={s.lb12}>{t('hs.due')}</span></div>
            <div className={s.who}>{t('hs.confirmer')} <b>[OPS-DOC] NHIDTT</b></div>
            {log.map(([k, when, c]) => (
              <div key={k} className={s.log}><span className={s.dotL} style={{ background: DOT[c] }} /><div><div>{k}</div><div className={s.lb12}>{when}</div></div></div>
            ))}
            <div className={s.cfBtns}>
              <Button icon={<IconArrowBackUp size={16} aria-hidden="true" />} disabled={act} onClick={() => { setCf('back'); toast(t('hs.returnedTo')); }}>{t('hs.cfBack')}</Button>
              <Button variant="primary" className={s.grow} disabled={act} onClick={() => { setCf('ok'); toast(t('hs.confirmedToast', { code })); }}>{cf === 'ok' ? t('hs.cfOk') : t('hs.confirmBtn', { code })}</Button>
            </div>
          </section>
        </div>
        <div className={s.right}>
          {state === 'loading' && <StateView state="loading" skeleton={<Skeleton />} />}
          {state === 'empty' && <StateView state="empty" title={t('hs.emptyT')} desc={t('hs.emptyD')} icon={<IconSparkles size={26} aria-hidden="true" />} />}
          {state === 'error' && <StateView state="error" title={t('hs.errT')} desc={t('hs.errD')} action={<Button onClick={() => location.reload()}>{t('hs.retry')}</Button>} />}
          {result && (
            <>
              <section aria-label={t('hs.candSec')} className={`${s.card} ${s.pad16}`}>
                <h2>{t('hs.candH')}</h2>
                <div role="radiogroup" aria-label={t('hs.candSec')} className={s.cands}>
                  {CANDS.map(([c, name, conf, why], i) => (
                    <button key={c} type="button" role="radio" aria-checked={pick === i} className={`${s.cand} ${pick === i ? s.candOn : ''}`} onClick={() => setPick(i)}>
                      <span className={s.radio} />
                      <div className={s.cc}><b>{c}</b><div className={s.conf}><span className={s.bar}><span style={{ width: `${conf}%`, background: conf > 50 ? 'var(--ok)' : 'var(--warn)' }} /></span><span className={s.lb12}>{conf}%</span></div></div>
                      <div className={s.cn}><div className={s.nm}><HsName code={c} fallback={name} /></div><div className={s.why}>{why}</div></div>
                    </button>
                  ))}
                </div>
              </section>
              <section aria-label={t('hs.tariffSec')} className={`${s.card} ${s.pad16}`}>
                <div className={s.hrow}><h2>{t('hs.taxH', { code })}</h2><span className={s.lb12}>{dir === 'NK' ? t('hs.taxImp', { o: ori }) : t('hs.taxExp', { o: ori })}</span></div>
                <div className={s.taxes}>
                  {cards.map((x) => <div key={x.k} className={`${s.tx} ${x.hi ? s.txHi : ''}`}><div className={s.lb12}>{x.k}</div><div className={s.txV}>{x.v}</div><div className={s.lb12}>{x.s}</div></div>)}
                </div>
                <div className={s.policy}>
                  <b>{t('hs.policy')}</b>
                  {POLICY[pick]!.map(([k, c]) => <div key={k} className={s.pol}><span className={s.dotL} style={{ background: DOT[c] }} /><span>{k}</span></div>)}
                </div>
              </section>
              <section aria-label={t('hs.ftaSec')} className={s.card}>
                <div className={`${s.hrow} ${s.ftaH}`}>
                  <div><h2>{t('hs.ftaH')}</h2><div className={s.lb12}>{dir === 'NK' ? t('hs.ftaImp', { o: ori }) : t('hs.ftaExp', { o: ori })}</div></div>
                  <Chip tone="ok">{fta.bestK}</Chip>
                </div>
                <table className={s.tbl}>
                  <thead><tr><th>{t('hs.thAgr')}</th><th>{t('hs.thForm')}</th><th className={s.r}>{t('hs.thTax')}</th><th>{t('hs.thRule')}</th><th>{t('hs.thApply')}</th></tr></thead>
                  <tbody>
                    {fta.rows.map((f) => (
                      <tr key={f.k} className={`${f.best ? s.best : ''} ${f.ok ? '' : s.dim}`}>
                        <td><b>{f.k}</b></td>
                        <td><span className={s.form}>{f.form}</span></td>
                        <td className={`${s.r} ${s.rate} ${f.best ? s.okTx : ''}`}>{f.rate}</td>
                        <td className={s.rule}>{f.rule}</td>
                        <td className={`${s.ap} ${f.best ? s.okTx : ''}`}>{f.s}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          )}
        </div>
      </div>
      {dock && (
        <aside className={s.dock} aria-label={t('hs.recent')}>
          <Segmented label={t('hs.lookup')} value={dock} onChange={setDock} items={[{ key: 'recent', label: t('hs.recent') }, { key: 'pend', label: t('hs.pend') }]} />
          {list.map(([c, k, m, st, tone]) => (
            <div key={c + k} className={s.dkRow}><b>{c}</b><div>{k}</div><div className={s.lb12}>{m}</div><span style={{ color: DOT[tone] }}>{st}</span></div>
          ))}
        </aside>
      )}
      </div>
    </Page>
  );
}
